import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import { DimensionValueChart, DimensionValueFilter, MetricBreakdownResponse, ValueText } from "../../../models";
import { LanguageService, MetricApiService } from "../../../services";
import { FormDropdownComponent, FormDropdownMultiComponent } from "../../form";

@Component({
  selector: "adaa-metric-breakdown-modal",
  imports: [TranslatePipe, FormDropdownComponent, FormDropdownMultiComponent],
  templateUrl: "./metric-breakdown-modal.html",
  styleUrl: "./metric-breakdown-modal.scss",
})
export class MetricBreakdownModal implements OnInit {
  private readonly _metricApiService = inject(MetricApiService);
  readonly modal = inject(NgbActiveModal);
  readonly languageService = inject(LanguageService);

  //Inputs
  modalTitle = signal<string>("");
  periodId = signal<number | undefined>(undefined);
  metricId = signal<number | undefined>(undefined);
  kpiId = signal<number | undefined>(undefined);
  periodsDdl = signal<ValueText[]>([]);

  filters = signal<DimensionValueFilter[]>([]);
  breakdownData = signal<MetricBreakdownResponse[]>([]);

  readonly chartsConfig = computed<DimensionValueChart[]>(() => this._buildCharts(this.breakdownData()));

  private readonly _accents = ["#d29f35", "#6f7e95", "#29a9e0"];
  private readonly _fullColumnThreshold = 8;
  private readonly _tickFractions = [0, 0.25, 0.5, 0.75, 1];

  public ngOnInit() {
    this._init();
  }

  public applyFilter(value: number[], filter: DimensionValueFilter) {
    filter.value = value;
    this._getData();
  }

  public periodChanged(value: number) {
    this.periodId.set(value);
    this._getData();
  }

  private _init() {
    const periodId = this.periodId();
    if (!periodId) return;

    this._metricApiService.getMetricBreakdown(periodId, this.kpiId(), this.metricId(), undefined).subscribe({
      next: (response) => {
        this._buildFilter(response.responseData ?? []);
        this.breakdownData.set(response.responseData ?? []);
      },
    });
  }

  private _buildFilter(data: MetricBreakdownResponse[]) {
    const filters: DimensionValueFilter[] = [];

    data.forEach((item) => {
      const dimension = filters.find((f) => f.dimensionId === item.dimensionId);
      if (dimension) {
        dimension.options.push({
          text: AdaaHelper.getItemValueByToken(item, "dimensionValueName"),
          value: item.dimensionValueId,
        });
      } else {
        filters.push({
          value: [],
          dimensionId: item.dimensionId,
          dimensionName: AdaaHelper.getItemValueByToken(item, "dimensionName"),
          options: [
            {
              text: AdaaHelper.getItemValueByToken(item, "dimensionValueName"),
              value: item.dimensionValueId,
            },
          ],
        });
      }
    });
    this.filters.set(filters);
  }

  private _buildCharts(data: MetricBreakdownResponse[]): DimensionValueChart[] {
    const dimensions = new Map<number, MetricBreakdownResponse[]>();

    data.forEach((item) => {
      const values = dimensions.get(item.dimensionId);
      if (values) values.push(item);
      else dimensions.set(item.dimensionId, [item]);
    });

    return [...dimensions.entries()]
      .sort(([, a], [, b]) => a.length - b.length)
      .map(([dimensionId, values], index) =>
        this._buildChart(dimensionId, values, this._accents[index % this._accents.length])
      );
  }

  private _buildChart(id: number, values: MetricBreakdownResponse[], accent: string): DimensionValueChart {
    const rows = [...values].sort((a, b) => b.value - a.value);
    const max = this._niceMax(Math.max(...rows.map((item) => item.value)));
    const isFullColumn = rows.length > this._fullColumnThreshold;

    return {
      id,
      title: AdaaHelper.getItemValueByToken(values[0], "dimensionName"),
      isFullColumn,
      ticks: this._tickFractions.map((fraction) => this._formatValue(max * fraction)),
      rows: rows.map((item, index) => ({
        label: AdaaHelper.getItemValueByToken(item, "dimensionValueName"),
        value: item.value,
        valueText: this._formatValue(item.value),
        percentage: Math.max((item.value / max) * 100, 0.4),
        color: this._getRowColor(accent, index),
      })),
    };
  }

  private _getRowColor(accent: string, index: number): string {
    if (index === 0) return accent;
    return AdaaHelper.hexToRgba(accent, "0.6");
  }

  private _niceMax(max: number): number {
    if (max <= 0) return 1;

    const power = Math.pow(10, Math.floor(Math.log10(max)));
    const normalized = max / power;

    if (normalized <= 1) return power;
    if (normalized <= 2) return 2 * power;
    if (normalized <= 2.5) return 2.5 * power;
    if (normalized <= 5) return 5 * power;
    return 10 * power;
  }

  private _formatValue(value: number): string {
    if (value >= 1e12) return `${this._trimZeros((value / 1e12).toFixed(2))}T`;
    if (value >= 1e9) return `${this._trimZeros((value / 1e9).toFixed(2))}B`;
    if (value >= 1e6) return `${this._trimZeros((value / 1e6).toFixed(2))}M`;
    if (value >= 1e3) return `${this._trimZeros((value / 1e3).toFixed(1))}K`;
    return this._trimZeros(value.toFixed(2));
  }

  private _trimZeros(value: string): string {
    if (!value.includes(".")) return value;
    return value.replace(/\.?0+$/, "");
  }

  private _getData() {
    const periodId = this.periodId();
    if (!periodId) return;

    const dimensionsValues: number[] = [];
    this.filters().forEach((e) => {
      dimensionsValues.push(...e.value);
    });

    this._metricApiService.getMetricBreakdown(periodId, this.kpiId(), this.metricId(), dimensionsValues).subscribe({
      next: (response) => {
        this.breakdownData.set(response.responseData ?? []);
      },
    });
  }
}
